import { Messages } from '../../codegen/Localize';
import { useFormikContext } from '../FormikWrapperForStubbing';
import { useCallback } from 'react';
import { appStore } from '../../app/AppStore';
import { AppState } from '../../app/state/AppState';
import * as React from 'react';

/**
 * Placeholder function to use selectors without a direct dependency on redux store. This is implemented
 * in later versions of react redux
 * @param selector
 */
export function useSelector<T>(selector: (state: AppState) => T): T {
  return selector(appStore.getState());
}

/**
 * React hook to handle change from some shared widgets such as checkbox and radiobutton, since they don't pass
 * a standard event
 * @param fieldName The formik field name to use
 */
export function useSharedWidgetChangeHandler<FieldType = boolean, Values = any>(fieldName: string) {
  const formik = useFormikContext<Values>();
  return useCallback(
    (value: FieldType) => formik.setFieldValue(fieldName, value),
    [formik?.setFieldValue]);
}

/**
 * A composite formik validator hook. Returns the first error string returned by the validators, in the specified order.
 * @param validators An array of formik validators. Each validator should return null if the value is valid, or an error
 *                   string if the value is invalid
 */
export function useCompositeValidator<T>(...validators: Array<(value: T) => string>) {
  return React.useCallback((value: T) => {
    for (let i = 0; i < validators.length; i++) {
      const result = validators[i](value);

      if(result !== null) {
        return result;
      }
    }
    return null;
  }, validators);
}

/**
 * A numeric range formik validator hook.
 * @param min - minimum value
 * @param minMessage - a function which returns the error message if the value is less than min
 * @param max - maximum value
 * @param maxMessage - a function which returns the error message if the value is greater than max
 */
export function useRangeValidator(min: number, minMessage: () => string,
                                  max: number, maxMessage: () => string) {
  return React.useCallback((userCount: number) => {
    if(userCount > max) {
      return maxMessage();
    }

    if (userCount < min) {
      return minMessage();
    }

    return null;
  }, [min, minMessage, max, maxMessage]);
}

const intRegex = /^[0-9]*$/;

/**
 * An integer validator hook. Parses the passed string/number and returns the specified error message if it's not a positive integer
 * @param invalidMessage
 */
export function usePositiveIntegerValidator(invalidMessage: () => string) {
  return React.useCallback((num: string | number) => {
    if (intRegex.test((num ?? '') + '')) {
      return null;
    }

    return invalidMessage();
  }, [invalidMessage]);
}

/**
 * A function that tests if a number is a positive, finite number
 * @param num
 */
function isPositiveNumber(num: number) {
  return !isNaN(num) && num > 0 && isFinite(num);
}

/**
 * A component validator which wraps another validator and returns null if disabled is true
 */
export function useValidatorWithDisable<T>(disabled: boolean, validator: (value: T) => string) {
  return React.useCallback((value: T) => disabled ? null : validator(value), [disabled, validator]);
}
